import React from 'react';
import { User, Sparkles, Link2 } from 'lucide-react';
import { SmartChatMessage } from '@/types/smartChat';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface ChatMessageProps {
  message: SmartChatMessage;
}

export const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const isUser = message.role === 'user';
  const formatTime = (date: Date) => {
    return new Intl.DateTimeFormat('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  return (
    <div className={cn("flex items-start gap-3", isUser && "flex-row-reverse")}>
      {/* Avatar */}
      <div className={cn(
        "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0",
        isUser 
          ? "bg-primary text-white" 
          : "bg-gradient-to-r from-primary/20 to-accent/20 text-primary"
      )}>
        {isUser ? (
          <User className="w-4 h-4" />
        ) : (
          <Sparkles className="w-4 h-4" />
        )}
      </div>

      {/* Message Content */}
      <div className={cn("flex-1 max-w-[85%]", isUser && "flex flex-col items-end")}>
        {/* Message Bubble */}
        <div className={cn(
          "rounded-2xl p-3 break-words",
          isUser
            ? "bg-primary text-white rounded-tr-sm"
            : "bg-muted/50 text-foreground rounded-tl-sm"
        )}>
          <div className="text-sm leading-relaxed whitespace-pre-wrap">
            {message.content}
          </div>
        </div>

        {/* Message Metadata */}
        <div className={cn(
          "flex items-center gap-2 mt-1 text-xs text-muted-foreground",
          isUser && "flex-row-reverse"
        )}>
          <span>{formatTime(message.timestamp)}</span>
          
          {/* Context Used Indicator */}
          {message.contextUsed && message.pageContext && (
            <Badge variant="secondary" className="text-xs h-5 px-2">
              <Link2 className="w-3 h-3 mr-1" />
              Contextual
            </Badge>
          )}
        </div>

        {/* Page Context Info (for debugging) */}
        {!isUser && message.pageContext && message.contextUsed && (
          <div className="mt-2 p-2 bg-muted/30 rounded-lg text-xs text-muted-foreground">
            <div className="flex items-center gap-1 mb-1">
              <Link2 className="w-3 h-3" />
              <span className="font-medium">Baseado em:</span>
            </div>
            <div>{message.pageContext.title} ({message.pageContext.pageType})</div>
          </div>
        )}
      </div>
    </div>
  );
};